import { message } from 'antd';
import apiFetch from '../apiFetch';
import { fetchActiveRescueData } from './rescueDataApi';
import {
  displayRescueTargetOnMap as displayRescueTargetOnMapUtil,
  displayRescueTrackOnMap as displayRescueTrackOnMapUtil,
  showRescueTargetHoverTooltip,
  showRescueTrackHoverTooltip,
  showRescueTargetsListTooltip,
  showRescueTracksListTooltip,
  clearRescueDataFromMap,
  removeRescueTargetMarkerFromMap,
  removeRescueTrackFromMap as removeRescueTrackLayerFromMap
} from './rescueMapLayers';
import { commandPinCoversLatLng } from './commandPinHelpers';

/**
 * 指挥系统：救援目标/轨迹的加载、上图、悬停列表、清理与单个移除
 * 自 pages/DashboardAuthenticated.js 原样迁出；每次渲染按当前状态重新生成（与拆分前相同）。
 */
export function createDashboardRescueActions({
  mapRef,
  rescueDisplayLngLatCacheRef,
  rescueTargetMarkersRef,
  rescueTrackLinesRef,
  rescueTargets,
  rescueTracks,
  setRescueTargets,
  setRescueTracks,
  /** 可选：指令点 Map，发送位置双写时与 SOS 指令点去重 */
  commandPinsMapRef
}) {
  // 加载救援数据
  const loadRescueData = async (options = {}) => {
    try {
      console.log('开始加载救援数据...');
      const map = mapRef?.current;
      try {
        if (map && typeof map.setProjection === 'function') {
          const proj = map.getProjection?.();
          if (proj && String(proj.name || proj).toLowerCase() === 'globe') {
            map.setProjection('mercator');
          }
        }
      } catch (_) { /* ignore */ }
      const { targets: activeTargets, tracks: activeTracks } = await fetchActiveRescueData(apiFetch);
      setRescueTargets(activeTargets);
      console.log('加载救援目标:', activeTargets.length, '个');
      activeTargets.forEach((target) => displayRescueTargetOnMap(target));
      setRescueTracks(activeTracks);
      console.log('加载救援轨迹:', activeTracks.length, '个');
      activeTracks.forEach((track) => displayRescueTrackOnMap(track));
      console.log('救援数据加载完成');
      // quiet：地图初始化/切底图自动重载不弹；用户点「加载救援」仍提示
      if (!options?.quiet && (activeTargets.length || activeTracks.length)) {
        message.success(`已加载救援点位 ${activeTargets.length}、轨迹 ${activeTracks.length}`);
      }
    } catch (error) {
      console.error('加载救援数据失败:', error);
      if (!options?.quiet) {
        try { message.error('加载救援数据失败'); } catch (_) { /* ignore */ }
      }
    }
  };

  // 在地图上显示救援目标（与 command-pins 同坐标则跳过，避免双 SOS）
  const displayRescueTargetOnMap = (targetData) => {
    if (!targetData) return;
    if (commandPinCoversLatLng(commandPinsMapRef?.current, targetData.lat, targetData.lng)) {
      return;
    }
    const cached = targetData?.id != null
      ? rescueDisplayLngLatCacheRef.current.get(String(targetData.id))
      : null;
    displayRescueTargetOnMapUtil(mapRef.current, targetData, {
      markersRef: rescueTargetMarkersRef,
      onHoverShowList: showRescueTargetList,
      displayLngLat: cached || undefined
    });
  };

  // 在地图上显示救援轨迹
  const displayRescueTrackOnMap = (trackData) => {
    displayRescueTrackOnMapUtil(mapRef.current, trackData, {
      trackLinesRef: rescueTrackLinesRef,
      onHoverShowList: showRescueTrackList
    });
  };

  // 显示救援目标列表
  const showRescueTargetList = (targetData, element) => {
    return showRescueTargetHoverTooltip(targetData, element);
  };

  // 显示救援目标列表（按钮悬停）
  const showRescueTargetsList = (buttonElement) => {
    showRescueTargetsListTooltip(buttonElement, rescueTargets);
  };

  // 显示救援轨迹列表（按钮悬停）
  const showRescueTracksList = (buttonElement) => {
    showRescueTracksListTooltip(buttonElement, rescueTracks);
  };

  // 显示救援轨迹列表
  const showRescueTrackList = (trackData, element) => {
    return showRescueTrackHoverTooltip(trackData, element);
  };

  // 清理救援数据
  const clearRescueData = () => {
    clearRescueDataFromMap(mapRef.current, rescueTargetMarkersRef, rescueTrackLinesRef);
    setRescueTargets([]);
    setRescueTracks([]);
  };

  // 从指挥系统地图移除单个救援目标（完结后）
  const removeRescueTargetFromMap = (targetId) => {
    if (!targetId) return;
    removeRescueTargetMarkerFromMap(rescueTargetMarkersRef, targetId);
    setRescueTargets(prev => prev.filter(t => String(t.id) !== String(targetId)));
  };

  // 从指挥系统地图移除单个救援轨迹（完结后）
  const removeRescueTrackFromMap = (trackIdValue) => {
    if (!trackIdValue || !mapRef.current) return;
    removeRescueTrackLayerFromMap(mapRef.current, rescueTrackLinesRef, trackIdValue);
    setRescueTracks(prev => prev.filter(t => String(t.id) !== String(trackIdValue)));
  };

  return {
    loadRescueData,
    displayRescueTargetOnMap,
    displayRescueTrackOnMap,
    showRescueTargetList,
    showRescueTargetsList,
    showRescueTracksList,
    showRescueTrackList,
    clearRescueData,
    removeRescueTargetFromMap,
    removeRescueTrackFromMap
  };
}
